import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, message, UploadFile } from 'antd';
import { Button, Checkbox, Icon, Input, Spinner, Switch, TextArea } from 'ui';
import useQueryApiClient from 'utils/useQueryApiClient';
import { QUALITY_MARK_ASPECT_RATIO } from 'config/config';
import { FileUploader } from '../../../../ui/UploadImage/FileUploader';
import { StyledQualityMarkDrawer } from './style';
import { RcFile } from 'antd/es/upload';

interface QualityMark {
  id: number;
  name: string;
  description: string;
  slug: string | null;
  logo: string;
  hidden: number;
  sort: number;
  product: boolean;
}

interface QualityMarkCreateUpdateDrawerProps {
  id?: number;
  ngType?: string;
  isDrawerVisible: boolean;
  onClose: Function;
}

const QualityMarkCreateUpdateDrawer = ({
  id,
  ngType,
  isDrawerVisible,
  onClose,
}: QualityMarkCreateUpdateDrawerProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [qmark, setQmark] = useState<QualityMark | undefined>(undefined);

  const { isLoading: isQmarkLoading } = useQueryApiClient({
    request: {
      url: `api/v2/qmark/${id}`,
    },
    enabled: !!id,
    onSuccess: (response) => handleQmark(response),
  });

  const { appendData: storeQmark } = useQueryApiClient({
    request: {
      url: id ? `api/v2/qmark/${qmark?.id}` : `api/v2/qmark`,
      method: 'POST',
      multipart: true,
    },
    onSuccess: () => {
      innerOnClose();
    },
    onError: () =>
      message.error(intl.formatMessage({ id: 'message.error_save' })),
  });

  const { appendData: fetchImage, isLoading: isGalleryLoading } =
    useQueryApiClient({
      request: {
        url: '/api/v2/image',
        multipart: true,
        disableOnMount: true,
      },
      onSuccess: (response) => handleRawFile(response),
    });

  const { data: checkboxOptions } = useQueryApiClient({
    request: {
      url: `api/v2/qmark/select`,
    },
  });

  const handleQmark = (respQmark: QualityMark) => {
    setQmark(respQmark);
    form.setFieldsValue(respQmark);
    if (respQmark.logo) {
      fetchImage({ path: respQmark.logo }, [], {
        logoUrl: respQmark.logo,
      });
    }
  };

  const handleRawFile = (imageRawFile: Blob) => {
    const fileName = qmark?.logo.split('/').pop() || 'unknown_file';

    const rcFile = new File([imageRawFile], fileName, {
      type: imageRawFile.type,
    }) as RcFile;

    rcFile.uid = `${Date.now()}-${Math.random()}`;

    const antdObject: UploadFile = {
      name: rcFile.name,
      status: 'done',
      url: URL.createObjectURL(rcFile),
      uid: rcFile.uid,
      originFileObj: rcFile,
      type: rcFile.type,
    };

    setFileList((prevFileList) => [...prevFileList, antdObject]);
  };

  const onFinish = async (values: any) => {
    try {
      await form.validateFields();

      let formData = new FormData();

      const rcFile = fileList[0]?.originFileObj;

      if (rcFile && rcFile instanceof Blob) {
        const realFile = new File([rcFile], rcFile.name, {
          type: rcFile.type,
          lastModified: rcFile.lastModified || Date.now(),
        });

        formData.append('logo_image', realFile);
      } else {
        throw new Error(intl.formatMessage({ id: 'error.add_images' }));
      }

      Object.entries(values).forEach(([key, value]) => {
        if (key === 'images') {
          return;
        }

        if (key === 'conflict') {
          formData.append(
            key,
            value ? JSON.stringify(value) : JSON.stringify([])
          );
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
        } else if (value != null) {
          formData.append(key, String(value));
        }
      });

      if (ngType) {
        formData.append('conflict', JSON.stringify([]));

        if (!id) {
          formData.append('ng_type', ngType);
        }
      }

      storeQmark(formData);
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  const innerOnClose = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  return (
    <StyledQualityMarkDrawer
      destroyOnClose={true}
      open={isDrawerVisible}
      onClose={() => innerOnClose()}
      title={
        id ? (
          <p>{intl.formatMessage({ id: 'general.edit' })}</p>
        ) : (
          <p>{intl.formatMessage({ id: 'general.add' })}</p>
        )
      }
      footer={
        <div className={'footer'}>
          <Button
            label={intl.formatMessage({ id: 'general.back' })}
            onClick={innerOnClose}
          />
          <Button
            label={intl.formatMessage({ id: 'general.submit' })}
            type="primary"
            htmlType="submit"
            onClick={() => form.submit()}
          />
        </div>
      }
      closeIcon={<Icon name={'global-close-icon'} />}
    >
      <Spinner spinning={isGalleryLoading || isQmarkLoading}>
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Input
            name={'name'}
            label={intl.formatMessage({ id: 'qmark.name' })}
            validations={'required'}
          />
          <TextArea
            name={'description'}
            label={intl.formatMessage({ id: 'qmark.description' })}
            validations={'required'}
          />
          {!ngType && (
            <>
              <Switch
                name={'product'}
                label={intl.formatMessage({ id: 'qmark.product' })}
              />
              <Checkbox
                name={'conflict'}
                formLabel={intl.formatMessage({ id: 'qmark.not_conflicted' })}
                options={checkboxOptions}
                breakNewLine
              />
            </>
          )}
          <FileUploader
            onFileListChange={setFileList}
            fileList={fileList}
            multiple={false}
            title={intl.formatMessage({ id: 'general.logo' })}
            isCrop={true}
            aspectRatio={QUALITY_MARK_ASPECT_RATIO}
          />
        </Form>
      </Spinner>
    </StyledQualityMarkDrawer>
  );
};

export default QualityMarkCreateUpdateDrawer;
